/**
 * lib/pastelLedger.ts
 * ------------------------------------------------------------------
 * FINANCE — reading Pastel's Customer Detailed Ledger, the one upload the
 * Pastel Check takes from 17 Oct 2026 (it replaced the age analysis).
 *
 * In Pastel 19: View → Customers → Detailed Ledger → By Customer, the
 * year to date, exported to Excel. Unlike the age analysis it carries
 * every account — settled ones and old ones too — and every document on
 * each: so a difference can be traced to the receipt or invoice behind
 * it, not just measured.
 *
 *   Livingstone Kolobeng College  |  Prepared by: …
 *   Customer Detailed Ledger 01/01/26 to 31/12/26
 *   Date | Entry Type | Reference | Contra Acc. | Description | Debit | Credit | Cumulative
 *        |            |           | (If Avail)  |
 *   MOK223 : MOKGETHWANE MOTHEO ARONA
 *   Opening Balance as at 01/01/26 | 0
 *   05/01/26 | Sales | IN147524 | | Invoice | 22000 | | 22000
 *   07/01/26 | Receipt Journal | RC181243 | 8050/000 | MOKGTHWANE ARONA- FEES | | 22000 | 0
 *   Closing Balance as at 31/12/26 | 0
 *   …
 *   TOTAL CLOSING BALANCE AS AT 31/12/26 | 3032770.51
 *
 * An opening or closing balance sits in the Debit column when owed and the
 * Credit column when in credit. Positive = the customer owes, as in the
 * portal, so the two subtract directly.
 *
 * Each account's lines must add up to its closing balance; one that does
 * not is reported, never silently accepted. The 16 Oct 2026 export
 * (1,143 accounts, 7,349 lines) ties exactly.
 * ------------------------------------------------------------------ */
import type { PastelRow } from '@/lib/pastelAgeAnalysis';

/** What a ledger line is, read from Pastel's entry type and contra account. */
export type LedgerKind =
  /** Sales — an invoice. */
  | 'invoice'
  /** Sales Credits — a credit note. */
  | 'credit'
  /** Money in (or back out): Receipt Journal, CB … Receipts, and customer
   *  journals to 0000/000 or 8050/000 — receipts moved between accounts. */
  | 'receipt'
  /** Customer journal to 3550/000 — a discount. */
  | 'discount'
  /** Any other customer journal. */
  | 'journal';

export interface LedgerLine {
  /** YYYY-MM-DD. */
  date: string;
  kind: LedgerKind;
  /** Pastel's entry type as printed, e.g. "Receipt Journal". */
  type?: string;
  /** IN147524, RC181243, DISC, … */
  ref: string;
  description?: string;
  /** Debit − credit: positive charges the customer, negative credits them. */
  amount: number;
}

export interface LedgerAccount {
  code: string;
  name: string;
  opening: number;
  closing: number;
  lines: LedgerLine[];
}

export interface PastelLedger {
  /** "Customer Detailed Ledger 01/01/26 to 31/12/26". Text only. */
  title: string;
  accounts: LedgerAccount[];
  /** Σ closing balances. */
  total: number;
  warnings: string[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const txt = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());
const num = (v: unknown) => { const n = Number(String(v ?? '').replace(/[,\s]/g, '')); return Number.isFinite(n) ? n : 0; };

/** True when a sheet is Pastel's Customer Detailed Ledger. */
export function isPastelLedger(rows: unknown[][]): boolean {
  return rows.slice(0, 4).some((r) => /Customer Detailed Ledger/i.test(txt(r?.[0])));
}

export function ledgerKind(type: string, contra: string): LedgerKind {
  if (/^Sales$/i.test(type)) return 'invoice';
  if (/^Sales Credits$/i.test(type)) return 'credit';
  if (/Receipt Journal|^CB .*Receipts$/i.test(type)) return 'receipt';
  if (/^Customer Journals$/i.test(type)) {
    if (contra === '3550/000') return 'discount';
    if (contra === '0000/000' || contra === '8050/000') return 'receipt';
  }
  return 'journal';
}

/** "05/01/26" → "2026-01-05". */
function isoDate(d: string): string {
  return `20${d.slice(6, 8)}-${d.slice(3, 5)}-${d.slice(0, 2)}`;
}

export function parsePastelLedger(rows: unknown[][]): PastelLedger {
  if (!isPastelLedger(rows)) throw new Error('This is not Pastel\'s Customer Detailed Ledger.');
  const title = rows.slice(0, 4).map((r) => txt(r?.[0])).find((t) => /Customer Detailed Ledger/i.test(t)) ?? '';
  const headerIdx = rows.findIndex((r) => /^Date$/i.test(txt(r?.[0])) && /Entry Type/i.test(txt(r?.[1])));
  if (headerIdx < 0) throw new Error('No "Date | Entry Type" heading — this is not the Detailed Ledger.');
  // Opening and closing balances: Debit (col 1) when owed, Credit (col 2) when in credit.
  const balanceOf = (r: unknown[]) => round2(num(r[1]) - num(r[2]));

  const accounts: LedgerAccount[] = [];
  const warnings: string[] = [];
  let cur: LedgerAccount | null = null;
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i] ?? [];
    const first = txt(r[0]);
    if (!first || /^\(If Avail\)$/i.test(txt(r[3]))) continue;
    const head = first.match(/^(\S+)\s+:\s+(.*)$/);
    if (head && !txt(r[1])) {
      cur = { code: head[1].toUpperCase(), name: head[2].trim().replace(/\s+/g, ' '), opening: 0, closing: 0, lines: [] };
      accounts.push(cur);
      continue;
    }
    if (/^TOTAL CLOSING BALANCE/i.test(first)) continue;
    if (!cur) { warnings.push(`Row ${i + 1}: "${first}" comes before any account — skipped.`); continue; }
    if (/^Opening Balance/i.test(first)) { cur.opening = balanceOf(r); continue; }
    if (/^Closing Balance/i.test(first)) { cur.closing = balanceOf(r); continue; }
    if (!/^\d\d\/\d\d\/\d\d$/.test(first)) { warnings.push(`Row ${i + 1}: "${first}" was not read.`); continue; }
    const type = txt(r[1]);
    cur.lines.push({
      date: isoDate(first), kind: ledgerKind(type, txt(r[3])), type, ref: txt(r[2]),
      description: txt(r[4]) || undefined, amount: round2(num(r[5]) - num(r[6])),
    });
  }
  for (const a of accounts) {
    const sum = round2(a.opening + a.lines.reduce((t, l) => t + l.amount, 0));
    if (Math.abs(sum - a.closing) >= 0.01) warnings.push(`${a.code}: the entries add up to ${sum} but Pastel's closing balance is ${a.closing}.`);
  }
  return { title, accounts, total: round2(accounts.reduce((t, a) => t + a.closing, 0)), warnings };
}

/**
 * One comparison row per account, by closing balance — what the age
 * analysis's Total Due used to be. `activity` counts the year's entries,
 * so an old account with nothing this year can be told from a live one.
 */
export function ledgerRows(ledger: PastelLedger): PastelRow[] {
  return ledger.accounts.map((a) => ({ code: a.code, name: a.name, category: '', totalDue: a.closing, activity: a.lines.length }));
}

/* ------------------------------------------------------------------
   Kept with the upload, so the cards still have their evidence tomorrow.
   A Firestore document holds 1 MB; seven thousand lines in full would
   crowd that, so each is stored as one short line of text, and only from
   1 July — no portal opening balance is older than 11 July 2026.

   TEXT, NOT A LIST. Firestore refuses an array inside an array, and the
   first version stored each entry as a five-item list: the 19 Oct 2026
   upload failed with "Nested arrays are not supported". One string per
   entry — "MOK267|2026-09-08|R|RC186242|-10000" — is a flat list.
   ------------------------------------------------------------------ */
export type StoredLine = string;
const KIND_CODE: Record<LedgerKind, string> = { invoice: 'I', credit: 'C', receipt: 'R', discount: 'D', journal: 'J' };
const CODE_KIND = Object.fromEntries(Object.entries(KIND_CODE).map(([k, v]) => [v, k])) as Record<string, LedgerKind>;
export const STORED_LINES_FROM = '2026-07-01';
const SEP = '|';

export function storeLines(ledger: PastelLedger, from = STORED_LINES_FROM): StoredLine[] {
  return ledger.accounts.flatMap((a) => a.lines.filter((l) => l.date >= from)
    .map((l) => [a.code, l.date, KIND_CODE[l.kind], l.ref.split(SEP).join('/'), l.amount].join(SEP)));
}

export function linesByCode(stored: StoredLine[]): Map<string, LedgerLine[]> {
  const m = new Map<string, LedgerLine[]>();
  for (const s of stored) {
    if (typeof s !== 'string') continue;
    const [code, date, k, ref, amount] = s.split(SEP);
    (m.get(code) ?? m.set(code, []).get(code)!).push({ date, kind: CODE_KIND[k] ?? 'journal', ref, amount: Number(amount) || 0 });
  }
  return m;
}

export const LEDGER_KIND_LABEL: Record<LedgerKind, string> = {
  invoice: 'Invoice', credit: 'Credit note', receipt: 'Receipt', discount: 'Discount', journal: 'Journal',
};

/* ------------------------------------------------------------------
   Matching
   ------------------------------------------------------------------ */

/**
 * The date the portal's own record of a pupil starts: the day of the
 * Pastel balance its opening document was set to. 11 Aug 2026 for the
 * re-baseline, 11 Jul 2026 for the June load (its label says so). Entries
 * on or before it are inside the opening balance already.
 */
export function openingBaseDate(openingLabels: string[]): string {
  return openingLabels.some((l) => /11 July/i.test(l)) ? '2026-07-11' : '2026-08-11';
}

export interface MoneyMatch<P> {
  /** Pastel receipts after the opening that no portal payment answers. */
  missingFromPortal: LedgerLine[];
  /** Portal payments no Pastel receipt answers — someone's entry for Pastel to have, or a mistake. */
  missingFromPastel: P[];
}

/**
 * Twin Pastel's receipts with the portal's payments. A payment answers a
 * receipt of the same amount dated within 31 days of it, nearest first;
 * each answers one. Every receipt is tried — not just those after the
 * opening — so a portal payment dated back to the month it was really made
 * still finds its twin. The same rule as seed/sync-pastel-ledger.cjs
 * (HIGH-RISK-CHANGES.md §12).
 *
 * Pass only money that really arrived: a reversed payment and its reversal
 * answer nothing.
 */
export function matchMoney<P extends { id: string; amount: number; receivedAt: string }>(
  lines: LedgerLine[],
  payments: P[],
  since: string,
): MoneyMatch<P> {
  const used = new Set<string>();
  const missingFromPortal: LedgerLine[] = [];
  const pays = payments.map((p) => ({ p, amount: round2(Number(p.amount) || 0), day: Date.parse(String(p.receivedAt).slice(0, 10)) }));
  for (const l of lines.filter((x) => x.kind === 'receipt')) {
    const want = round2(-l.amount);
    const day = Date.parse(l.date);
    let best: string | null = null;
    let bestGap = Infinity;
    for (const { p, amount, day: pd } of pays) {
      if (used.has(p.id) || Math.abs(amount - want) >= 0.005) continue;
      const gap = Math.abs(pd - day) / 864e5;
      if (gap <= 31 && gap < bestGap) { best = p.id; bestGap = gap; }
    }
    if (best) used.add(best);
    else if (l.date > since && want > 0) missingFromPortal.push(l);
  }
  const missingFromPastel = pays.filter(({ p, amount }) => !used.has(p.id) && amount !== 0).map(({ p }) => p);
  return { missingFromPortal, missingFromPastel };
}

/** Pastel receipts after `since` that no portal payment answers (matchMoney). */
export function receiptsMissingFromPortal(
  lines: LedgerLine[],
  payments: { id: string; amount: number; receivedAt: string }[],
  since: string,
): LedgerLine[] {
  return matchMoney(lines, payments, since).missingFromPortal;
}

/**
 * Portal invoices Pastel never charged: no Pastel invoice of the same
 * amount within 45 days of it. Pass the ones to ask about — the Pastel
 * Check passes those raised by hand, since a billing-run invoice Pastel
 * lacks is a different question (the run, not a person).
 */
export function chargesMissingFromPastel<I extends { id: string; total: number; issuedAt?: string }>(
  lines: LedgerLine[],
  invoices: I[],
): I[] {
  const used = new Set<LedgerLine>();
  const out: I[] = [];
  for (const inv of invoices) {
    const day = Date.parse(String(inv.issuedAt ?? '').slice(0, 10));
    const twin = lines.find((l) => l.kind === 'invoice' && !used.has(l) && Math.abs(l.amount - (Number(inv.total) || 0)) < 0.005
      && (!Number.isFinite(day) || Math.abs(Date.parse(l.date) - day) / 864e5 <= 45));
    if (twin) used.add(twin); else out.push(inv);
  }
  return out;
}

/**
 * Of the Pastel receipts the portal lacks, those it lacks because someone
 * REVERSED the payment in the portal: a reversed payment of the same
 * amount within 31 days. Pastel kept the receipt; the portal undid it.
 */
export function reversedStillInPastel<P extends { id: string; amount: number; receivedAt: string }>(
  missingFromPortal: LedgerLine[],
  reversedPayments: P[],
): { payment: P; receipt: LedgerLine }[] {
  const used = new Set<string>();
  const out: { payment: P; receipt: LedgerLine }[] = [];
  for (const l of missingFromPortal) {
    const p = reversedPayments.find((x) => !used.has(x.id) && Math.abs((Number(x.amount) || 0) + l.amount) < 0.005
      && Math.abs(Date.parse(String(x.receivedAt).slice(0, 10)) - Date.parse(l.date)) / 864e5 <= 31);
    if (p) { used.add(p.id); out.push({ payment: p, receipt: l }); }
  }
  return out;
}

/** The last day a ledger's entries reach — the day it was run to, near enough. */
export function ledgerEnd(lines: Iterable<LedgerLine[]>): string | undefined {
  let end: string | undefined;
  for (const ls of lines) for (const l of ls) if (!end || l.date > end) end = l.date;
  return end;
}
